<script>
import ApexCharts from "apexcharts";
import { VesselService } from "@/services/VesselService";
import AppLoading from "@/components/elements/AppLoading.vue";
import {permissionMixin} from "@/mixins/permissionMixins";

export default {
  name: "VesselShipBuilderGraph",
  mixins:[permissionMixin],
  props:{
    summaryPrint:{
      type: Boolean,
      default: false,
    },
  },
  components: { AppLoading },
  data() {
    return {
      loading: false,
      loadingModal: false,
      series: [],
      rawData: [],
      selectedShipbuilder: null,
      selectedDetails: {},
      bracketClicked: null,
      showModal: false,
      chartOptions: {
        chart: {
          type: "bar",
          stacked: true,
          horizontal: true,
          toolbar: { show: false },
          zoom: { enabled: false },
          events: {
            mounted: () => {
              this.$nextTick(() => {
                this.colorTotalsRed();
                this.addYAxisClickHandler();
              });
            },
            updated: () => {
              this.$nextTick(() => {
                this.colorTotalsRed();
                this.addYAxisClickHandler();
              });
            },
            dataPointSelection: (event, chartContext, config) => {
              this.handleDataPointSelection(event, chartContext, config);
            },
          },
        },
        grid: {
          padding: { left: 10, right: 10 },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: "75%",
            borderRadius: 4,
          },
        },
        dataLabels: {
          enabled: true,
          style: { fontSize: "11px", colors: ["#fff"] },
        },
        stroke: { show: true, width: 1, colors: ["#fff"] },
        yaxis: {
          title: { text: "SHIP BUILDERS" },
          labels: {
            show: true,
            align: "right",
            minWidth: 0,
            maxWidth: 750,
            style: { fontSize: "11px" },
            trim: false,
            formatter: (val) => val,
          },
        },
        xaxis: {
          title: { text: "VESSEL COUNT" },
          categories: [],
          labels: {
            style: { fontSize: "11px" },
          },
        },
        legend: { position: "bottom", horizontalAlign: "center" },
        tooltip: { y: { formatter: (val) => `${val} Vessel/s` } },
      },
    };
  },

  mounted() {
    this.$nextTick(() => {
      this.addYAxisClickHandler();
    });
  },

  methods: {
    closeModal() {
      this.showModal = false;
      this.selectedShipbuilder = null;
      this.selectedDetails = {};
      this.bracketClicked = null;
    },

    async getVesselData() {
      try {
        this.loading = true;
        const resp = await VesselService.getVesselShipBuilderData();
        const data = resp.data?.data || resp?.data || resp;

        this.rawData = data;

        const ageBrackets = [
          "0–4 yrs",
          "5–9 yrs",
          "10–14 yrs",
          "15–19 yrs",
          "20–24 yrs",
          "25–29 yrs",
          "30+ yrs",
        ];
        const totals = data.map((item) =>
          ageBrackets.reduce((sum, key) => sum + (Number(item[key]) || 0), 0)
        );
        const categories = data.map(
          (item, idx) => `${item.name} (${totals[idx]})`
        );
        const series = ageBrackets.map((bracket) => ({
          name: bracket,
          data: data.map((item) => Number(item[bracket]) || 0),
        }));

        this.series = series;
        this.chartOptions = {
          ...this.chartOptions,
          xaxis: {
            ...this.chartOptions.xaxis,
            categories,
          },
        };

        this.$nextTick(() => {
          if (this.$refs.vesselShipbuilderChart?.refresh) {
            this.$refs.vesselShipbuilderChart.refresh();
          }
          this.colorTotalsRed();
        });

        this.loading = false;
      } catch (error) {
        console.error("Error fetching shipbuilder data:", error);
      }
    },
    colorTotalsRed() {
      setTimeout(() => {
        const yLabels = document.querySelectorAll(".apexcharts-yaxis-label tspan");
        yLabels.forEach((el) => {
          const text = el.textContent;
          const match = text.match(/(.*)\s(\(\d+\))/);
          if (match) {
            const name = match[1];
            const total = match[2];
            el.textContent = "";
            const nameNode = document.createElementNS("http://www.w3.org/2000/svg", "tspan");
            nameNode.textContent = name + " ";
            nameNode.setAttribute("fill", "#000000");
            nameNode.setAttribute("font-weight", "normal");
            const totalNode = document.createElementNS("http://www.w3.org/2000/svg", "tspan");
            totalNode.textContent = total;
            totalNode.setAttribute("fill", "#d3313c");
            totalNode.setAttribute("font-weight", "700");
            el.appendChild(nameNode);
            el.appendChild(totalNode);
          }
        });
      }, 100);
    },

    handleDataPointSelection(event, chartContext, config) {
      const shipbuilderIndex = config.dataPointIndex;
      const bracketIndex = config.seriesIndex;
      if (
        shipbuilderIndex == null ||
        bracketIndex == null ||
        !this.chartOptions.xaxis.categories.length
      ) {
        return;
      }
      const builderName = this.chartOptions.xaxis.categories[shipbuilderIndex];
      const bracketName = this.series[bracketIndex].name;
      this.selectedShipbuilder = builderName;
      this.bracketClicked = bracketName;
      this.fetchShipbuilderVessels(builderName, bracketName);
    },
    async fetchShipbuilderVessels(shipbuilder, ageBracket) {
      try {
        this.loadingModal = true;
        const params = { shipbuilder, age_bracket: ageBracket };
        const response = await VesselService.getVesselShipBuilderList(params);
        this.selectedDetails = response.data.data || [];
        this.showModal = true;
        this.loadingModal = false;
      } catch (error) {
        console.error("Error fetching vessel list:", error);
      }
    },
    async showVessel(vesselId) {
      const route = this.$router.resolve({
        name: "VesselParticular",
        params: { id: vesselId },
      });
      window.open(route.href, "_blank");
    },
    formatDate(date) {
      if (!date) return "—";
      const d = new Date(date);
      if (isNaN(d)) return "—";
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
    },
    async getGraphImage() {
      const chart = this.$refs["vesselShipbuilderChart"].chart;
      const { imgURI } = await chart.dataURI();
      const byteString = atob(imgURI.split(',')[1]);
      const mimeString = imgURI.split(',')[0].split(':')[1].split(';')[0];
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const uintArray = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        uintArray[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([arrayBuffer], { type: mimeString });
      const formData = new FormData();
      formData.append("chart_image", blob, "chart.png");
      formData.append("report_type", 'SHIP TYPE');

      swal.fire({
        'title': `Exporting Vessel Graph Summary (SHIP TYPE)`,
        'text': "Please wait...",
        'imageUrl': "https://img.icons8.com/color/400/pie-chart--v1.png",
        'imageWidth': 100,
        'imageHeight': 100,
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false
      })
      swal.showLoading()
      const printResponse = await VesselService.printSummaryOfManagingVesselGraph(formData);
      swal.close()
      let url = window.URL.createObjectURL(new Blob([printResponse.data],{type:'application/pdf'}))
      let filename=`graph_by_ship_type_${Date.now()}.pdf`;
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
    },
    addYAxisClickHandler() {
      setTimeout(() => {
        const labels = document.querySelectorAll('.apexcharts-yaxis-label');
        labels.forEach((label, index) => {
          label.style.cursor = 'pointer';
          label.addEventListener('click', () => {
            const shipbuilder = this.chartOptions.xaxis.categories[index];
            this.selectedShipbuilder = shipbuilder;
            this.bracketClicked = null;
            this.fetchShipbuilderVessels(shipbuilder, null);
          });
        });
      }, 300);
    },
  },
  watch: {
    showModal(newVal) {
      if (newVal) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    },
  },
  async created() {
    await this.getVesselData();
  },
};
</script>
<template>
  <div class="card">
    <div class="card-body">
      <div v-if="!summaryPrint">
        <div class="row">
          <div class="col-8 mt-1">
            <span class="font-weight-bold text-uppercase h6">By Ship Builder</span>
          </div>
          <div class="col-4 text-right">
            <button class="btn-download force-white-all" @click="getGraphImage" v-if="['k-line-persons', 'k-line-persons2'].includes(user.roles_permissions?.roles[0]) === false">
              <font-awesome-icon class="mr-1" icon="download" /> Download Graph
            </button>
          </div>
        </div>
        <hr />
      </div>
      <template v-if="loading">
        <app-loading></app-loading>
      </template>
      <div class="row" v-else>
        <div class="col-12">
              <apexchart
                ref="vesselShipbuilderChart"
                type="bar"
                :options="chartOptions"
                :series="series"
                :height="1000"
              />
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div v-if="showModal" class="custom-modal-overlay">
      <div class="custom-modal">
        <div class="modal-header">
          <h5 class="mt-1 font-weight-bold">
            VESSELS (BY SHIP BUILDER) -
            <span class="text-danger text-uppercase font-weight-bold h5">
              {{ selectedShipbuilder ? selectedShipbuilder.replace(/\s*\(\d+\)\s*$/, '') : '' }}
            </span>
          </h5>
          <button class="close-btn font-weight-bold" @click="closeModal">×</button>
        </div>

        <div class="modal-body">
          <div class="row">
            <div class="col-12" v-if="loadingModal">
              <app-loading></app-loading>
            </div>
            <div class="col-12" v-else>
              <div class="table-responsive">
                <table class="table table-bordered">
                  <thead>
                  <tr>
                    <th
                      v-for="header in [
                          '#','Vessel Name','Flag','Age','Date of Build',
                          'Management Company','Management Office','Fleet','Action'
                        ]"
                      :key="header"
                      class="font-weight-bold text-uppercase text-center align-middle"
                      style="color: black; background-color: #e1e1e1;"
                    >
                      {{ header }}
                    </th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(vessel, index) in selectedDetails" :key="index">
                    <td class="align-middle text-center font-weight-bold">{{ index + 1 }}</td>
                    <td class="align-middle text-center text-uppercase">{{ vessel.vessel_name }}</td>
                    <td class="align-middle text-center text-uppercase">{{ vessel.flag }}</td>
                    <td class="align-middle text-center text-uppercase">{{ Math.floor(vessel.age) }} Years</td>
                    <td class="align-middle text-center text-uppercase">{{ formatDate(vessel.date_build) }}</td>
                    <td class="align-middle text-center text-uppercase">{{ vessel.management_company }}</td>
                    <td class="align-middle text-center text-uppercase">{{ vessel.management_office }}</td>
                    <td class="align-middle text-center text-uppercase">{{ vessel.fleet }}</td>
                    <td class="align-middle text-center">
                      <button class="e-btn e-btn-blue e-btn-sm e-text-white p-1" @click="showVessel(vessel.id)">
                        <font-awesome-icon icon="eye" /> View
                      </button>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.chart-wrapper {
  min-width: 1500px;
  display: inline-block;
}
.chart-wrapper .apexcharts-canvas,
.chart-wrapper .apexcharts-svg {
  width: 100% !important;
  min-width: 1500px !important;
}

/deep/ .apexcharts-yaxis-label span {
  white-space: normal !important;
  word-break: break-word !important;
}

/* Modal + buttons */
.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}
.custom-modal {
  background: #fff;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  animation: fadeIn 0.25s ease-in-out;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 15px;
  background: #ffffff;
}
.close-btn {
  background: none;
  border: none;
  color: red;
  font-size: 30px;
  cursor: pointer;
}
.btn-download {
  background: #4b5563;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
}
.btn-download:hover {
  background: #374151;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
